import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import { Icon } from './Icon';
import { theme } from '../../styles/theme';

const Nav = styled(motion.nav)<{ isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${({ isScrolled }) => 
    isScrolled 
      ? 'rgba(255, 255, 255, 0.95)' 
      : 'rgba(255, 255, 255, 0.1)'
  };
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ isScrolled }) => 
    isScrolled 
      ? theme.colors.gray[200] 
      : 'rgba(255, 255, 255, 0.1)'
  };
  padding: ${theme.spacing.md} 0;
  transition: all 0.3s ease-in-out;
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)<{ isScrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  text-decoration: none;
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${({ isScrolled }) => 
    isScrolled 
      ? theme.colors.primary 
      : theme.colors.white
  };
  transition: color 0.3s ease-in-out;
  
  img {
    height: 30px;
    width: auto;
  }
  
  &:hover {
    color: ${({ isScrolled }) => 
      isScrolled 
        ? theme.colors.secondary 
        : theme.colors.neutral
    };
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled.a<{ isScrolled: boolean }>`
  color: ${({ isScrolled }) => 
    isScrolled 
      ? theme.colors.gray[700] 
      : 'rgba(255, 255, 255, 0.9)'
  };
  text-decoration: none;
  font-weight: ${theme.typography.weights.medium};
  position: relative;
  padding: ${theme.spacing.sm} 0;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  
  &:hover {
    color: ${({ isScrolled }) => 
      isScrolled 
        ? theme.colors.primary 
        : theme.colors.white
    };
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${theme.colors.accent};
    transition: width 0.3s ease-in-out;
  }
  
  &:hover::after {
    width: 100%;
  }
`;



const MobileMenuButton = styled.button<{ isScrolled: boolean }>`
  display: none;
  background: none;
  border: none;
  color: ${({ isScrolled }) => 
    isScrolled 
      ? theme.colors.primary 
      : theme.colors.white
  };
  font-size: ${theme.typography.sizes.xl};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)<{ isScrolled: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ isScrolled }) => 
    isScrolled 
      ? 'rgba(255, 255, 255, 0.98)' 
      : 'rgba(2, 71, 174, 0.95)'
  };
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${({ isScrolled }) => 
    isScrolled 
      ? theme.colors.gray[200] 
      : 'rgba(255, 255, 255, 0.1)'
  };
  padding: ${theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.lg};
`;

const MobileNavLink = styled.a<{ isScrolled: boolean }>`
  color: ${({ isScrolled }) => 
    isScrolled 
      ? theme.colors.gray[700] 
      : theme.colors.white
  };
  text-decoration: none;
  font-weight: ${theme.typography.weights.medium};
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ isScrolled }) => 
    isScrolled 
      ? theme.colors.gray[200] 
      : 'rgba(255, 255, 255, 0.1)'
  };
  transition: color 0.3s ease-in-out;
  cursor: pointer;
  
  &:hover {
    color: ${theme.colors.accent};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;



interface NavbarProps {
  className?: string;
}

export const Navbar: React.FC<NavbarProps> = ({ className }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const isLandingPage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const landingPageLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Roadmap', href: '#roadmap' },
    { label: 'About', href: '#about' },
  ];

  const otherPageLinks = [
    { label: 'Home', href: '/' },
    { label: 'Features', href: '/' },
    { label: 'About', href: '/' },
  ];

  const currentLinks = isLandingPage ? landingPageLinks : otherPageLinks;

  return (
    <Nav
      isScrolled={isScrolled}
      className={className}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <NavContainer>
        <Logo to="/" isScrolled={isScrolled}>
          <img 
            src={isScrolled ? "/blue_transparent.png" : "/grey_transparent.png"} 
            alt="Care Plus Plus" 
          />
        </Logo>

        <NavLinks>
          {currentLinks.map((link) => (
            isLandingPage && link.href.startsWith('#') ? (
              <NavLink
                key={link.label}
                href={link.href}
                isScrolled={isScrolled}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href.substring(1));
                }}
              >
                {link.label}
              </NavLink>
            ) : (
              <Link key={link.label} to={link.href} style={{ textDecoration: 'none' }}>
                <NavLink
                  as="span"
                  isScrolled={isScrolled}
                  style={{ cursor: 'pointer' }}
                >
                  {link.label}
                </NavLink>
              </Link>
            )
          ))}
        </NavLinks>

        <MobileMenuButton
          isScrolled={isScrolled}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <Icon icon={isMobileMenuOpen ? FiX : FiMenu} />
        </MobileMenuButton>
      </NavContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            isScrolled={isScrolled}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {currentLinks.map((link) => (
              isLandingPage && link.href.startsWith('#') ? (
                <MobileNavLink
                  key={link.label}
                  href={link.href}
                  isScrolled={isScrolled}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.href.substring(1));
                  }}
                >
                  {link.label}
                </MobileNavLink>
              ) : (
                <Link key={link.label} to={link.href} style={{ textDecoration: 'none' }} onClick={() => setIsMobileMenuOpen(false)}>
                  <MobileNavLink
                    as="span"
                    isScrolled={isScrolled}
                    style={{ cursor: 'pointer' }}
                  >
                    {link.label}
                  </MobileNavLink>
                </Link>
              )
            ))}
            
          </MobileMenu>
        )}
      </AnimatePresence>
    </Nav>
  );
};
